// PickupPhoto — small avatar/thumbnail for an authorised pickup person.
// Renders a signed-URL <img> when a photo path exists, else an
// initials swatch. Mirrors ChildAvatar's caching pattern so multiple
// rows on the same screen don't trigger duplicate sign requests.

import { useEffect, useRef, useState } from "react";
import { getPickupPhotoUrl } from "@/lib/pickupAuthorizations";

const SWATCHES = [
  "bg-rose-100 text-rose-900",
  "bg-amber-100 text-amber-900",
  "bg-emerald-100 text-emerald-900",
  "bg-sky-100 text-sky-900",
  "bg-violet-100 text-violet-900",
  "bg-orange-100 text-orange-900",
];

function hash(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function initials(name) {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function PickupPhoto({
  path,
  name,
  size = 40,
  className = "",
  testId,
}) {
  const [url, setUrl] = useState(null);
  const [errored, setErrored] = useState(false);
  const reqId = useRef(0);

  useEffect(() => {
    let alive = true;
    setErrored(false);
    if (!path) {
      setUrl(null);
      return;
    }
    const myId = ++reqId.current;
    getPickupPhotoUrl(path).then((u) => {
      if (!alive || myId !== reqId.current) return;
      setUrl(u);
    });
    return () => {
      alive = false;
    };
  }, [path]);

  const px = `${size}px`;
  const fontPx = Math.max(10, Math.round(size * 0.4));
  const swatch = SWATCHES[hash(name || "?") % SWATCHES.length];

  if (path && url && !errored) {
    return (
      <img
        src={url}
        alt={name || "Pickup person"}
        loading="lazy"
        data-testid={testId}
        onError={() => setErrored(true)}
        style={{ width: px, height: px }}
        className={`shrink-0 rounded-full object-cover ${className}`}
      />
    );
  }
  return (
    <span
      role="img"
      aria-label={name || "Pickup person"}
      data-testid={testId}
      style={{ width: px, height: px, fontSize: `${fontPx}px` }}
      className={`flex shrink-0 items-center justify-center rounded-full font-semibold ${swatch} ${className}`}
    >
      {initials(name)}
    </span>
  );
}
